


















































import MarketSelect from '@/components/MarketSelect.vue';
import {TableHeaders} from '@/interfaces/components-data';
import {SelectMarket} from '@/interfaces/global-data';
import {DownLoadPercent, FilesPageParams, FileType, ServerFile} from '@/interfaces/server-data';
import {EventBus} from '@/mixins/event-bus';
import { localizeFileStatusMessage, isWritingToDatabase } from '@/utils/backendValueLocalizer';
import LocalData from '@/mixins/local-data';
import {BFormFile} from 'bootstrap-vue';
import {Component, Mixins, Prop, Watch} from 'vue-property-decorator';
import Http from '../mixins/http';
import Toast from '../mixins/toast';
import Card from '@/components/UI/Card.vue';

@Component({
    components: {
        MarketSelect,
        Card
    },
})
export default class Files extends Mixins(Http, Toast, LocalData) {
    @Prop() readonly type!: FileType;
    @Prop() readonly selectMarketProp?: SelectMarket | null = null;
    table: ServerFile[] = [];
    tableFields: TableHeaders[] = [
        {key: 'id', label: 'No.'},
        {key: 'tradingNetworks.tradingName', label: 'TC'},
        {key: 'filePath', label: 'File name'},
        {key: 'status_warnings', label: 'Status'},
        {key: 'fileUploadDate', label: 'Upload date and time'},
        {key: 'username', label: 'User'},
        {key: 'action', label: ''},
    ];

    computedTableFields: any[] = [
      {dataIndex: 'id', title: this.$t('uploads.table.id') as string, width: 100,},
      {dataIndex: 'tradingNetworks.tradingName', title: this.$t('uploads.table.tradingCompany') as string, ellipsis: true, width: 200},
      {dataIndex: 'filePath', title: this.$t('uploads.table.fileName') as string, ellipsis: true, width: 400},
      {dataIndex: 'status_warnings', title: this.$t('uploads.table.status') as string, ellipsis: true, customRender: (_: undefined, item: ServerFile) => this.showPercentsProgressOrStatus(item)},
      {dataIndex: 'fileUploadDate', title: this.$t('uploads.table.uploadDateTime') as string, ellipsis: true, width: 200},
      {dataIndex: 'username', title: this.$t('uploads.table.user') as string, ellipsis: true, width: 150},
      {dataIndex: 'action', title: '', scopedSlots: { customRender: 'action' }, width: 200},
    ];

    pagination: any = {
        pageSize: 20,
    };
    currentPage: number = 1;
    totalRows: number = 0;
    perPage: number = 20;
    progressCounter: number = 0;
    loadProgress: boolean = false;
    fileIdForDelete: number = -1;
    showDeleteFileModal: boolean = false;
    file: File | null = null;
    currentWarnings: string = '';
    deleteLoadingIds: number[] = [];

    selectMarket: SelectMarket | null = this.getMarketFromLocalstorage() || null;

    @Watch('selectMarketProp')
    handleSelectMarketChanged(value: SelectMarket): void {
        this.selectMarket = value;
    }

    disableDeleteButtonConditions(row: ServerFile): boolean {
        return row.status === 'IN_QUEUE_TO_DELETE' || row.status === 'DELETE_FILE' || row.status === 'DELETE_ON_ERROR'
    }

    getDeleteLoadingState(fileId: number): boolean {
        return this.deleteLoadingIds.includes(fileId);
    }

    setDeleteLoadingState(fileId: number, state: boolean) {
        if (state) {
            if (!this.deleteLoadingIds.includes(fileId)) {
                this.deleteLoadingIds.push(fileId);
            }
        } else {
            const index = this.deleteLoadingIds.indexOf(fileId);
            if (index > -1) {
                this.deleteLoadingIds.splice(index, 1);
            }
        }
    }

    getFiles(ctx: any, callback: (items: any[]) => void) {
        const pageParams: FilesPageParams = {
            type: this.type, size: ctx.perPage, page: ctx.currentPage - 1,
        };
        this.httpGetFileList(pageParams)
            .then((response) => {
                const pagination = { ...this.pagination };
                pagination.total = response.data.totalElements;
                this.pagination = pagination;
                this.table = response.data.content;
                this.totalRows = response.data.totalElements;
                callback(response.data.content);
            })
            .catch((e) => {
                this.showError(this.$t('uploads.messages.loadFilesError') as string);
                callback([]);
            });
    }

    updateSelectMarket(market: SelectMarket) {
        this.$log.debug('Files updateSelectMarket', market);
        this.selectMarket = market;
        localStorage.setItem('files_market', JSON.stringify(market))
    }

    uploadFile(event: Event) {
        event.preventDefault();
        if (!this.file) {
            this.showInfo(this.$t('uploads.messages.selectFile') as string);
            return;
        }
        if (!this.selectMarket || !this.selectMarket.id) {
            this.showInfo(this.$t('uploads.messages.selectTradingNetwork') as string);
            return;
        }
        const uploadFile = new FormData();
        this.loadProgress = true;
        const size = this.file.size;
        uploadFile.append('file', new Blob([this.file]), this.file.name);
        const self = this;
        const onUploadProgress = (e: any) => {
            const s = e.loaded / size;
            self.progressCounter = (s * 100) > 100 ? 100 : Math.round(s * 100);
        };

        this.httpUploadFile(this.selectMarket.id, this.type, uploadFile, onUploadProgress)
            .then(() => {
                this.getFiles({
                    currentPage: this.pagination.current,
                    perPage: this.pagination.pageSize,
                }, () => {});
                this.file = null;
                this.$nextTick(() => {
                    this.loadProgress = false;
                    this.progressCounter = 0;
                });
            })
            .catch((e) => {
                this.showError(this.$t('uploads.messages.uploadError') as string);
                this.loadProgress = false;
                this.progressCounter = 0;
                this.file = null;
                (this.$refs.fileinput as BFormFile).reset();
            });
    }

    refreshTable() {
        this.getFiles({
            currentPage: this.pagination.current,
            perPage: this.pagination.pageSize,
        }, () => {});
    }

    deleteFile(taskId: number) {
        this.setDeleteLoadingState(taskId, true);
        this.httpDeleteFile(taskId)
            .then(() => {
                this.table = this.table.filter((item) => {
                    return item.id !== taskId;
                });
                this.refreshTable();
                this.showInfo(this.$t('uploads.messages.fileDeleted') as string);
            })
            .catch((e) => {
                this.showError(this.$t('uploads.messages.deleteError') as string);
            })
            .finally(() => {
                this.setDeleteLoadingState(taskId, false);
            });
    }

    updateTableRowStatus(data: ServerFile) {
        const index = this.table.findIndex((item) => {
            return item.id === data.id;
        });
        if (index === -1) {
            this.table = [data, ...this.table];
            return;
        }
        this.table[index].statusMessage = data.statusMessage;
        if (data.warnings) {
            this.table[index].statusMessage += data.warnings;
        }
        this.refreshTable();
    }

    updateDownloadPercent(percent: DownLoadPercent) {
        const index = this.table.findIndex((item) => {
            return item.id === percent.fileId;
        });
        if (index !== -1) {
            this.table[index].progress = percent.percentDownload;
        }
    }

    beforeDeleteFileDialog(fileId: number) {
        this.fileIdForDelete = fileId;
        this.showDeleteFileModal = true;
    }

    showDeleteFileDialog(fileId: number) {
        this.deleteFile(fileId);
    }

    onTableChange(pagination: any) {
        const pager = { ...this.pagination };
        pager.current = pagination.current;
        this.pagination = pager;
        this.getFiles({
            currentPage: pagination.current,
            perPage: pagination.pageSize,
        }, () => {});
    }

    downloadTemplate(type: FileType) {
        this.$http.get('/api/file/template', {
            method: 'GET',
            responseType: 'blob',
            params: {
                type,
            },
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const excelFile = this.excelTypeValue(type) + '.xlsx';
            link.setAttribute('download', excelFile)
            document.body.appendChild(link);
            link.click();
        }).catch(() => {
            this.showError(this.$t('uploads.messages.downloadError') as string);
        });
    }

    excelTypeValue(type: FileType) {
        switch (type) {
            case FileType.BASE:
                return 'TemplateSales'
            case FileType.KVICARTS:
                return 'TemplateKviCarts'
            case FileType.COMPETITORS:
                return 'TemplateCompetitors'
            case FileType.PROMO:
                return 'TemplatePromo'
            case FileType.LASTPRICES:
                return 'TemplateLastPrices'
        }
    }

    downloadErrorLog(fileId: number) {
        this.$http.get('/api/error/list', {
            method: 'GET',
            responseType: 'blob',
            params: {
                fileId,
            },
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', this.$t('uploads.errorsFileName') as string);

            document.body.appendChild(link);
            link.click();
        })
            .catch((e) => {
                this.showError(this.$t('uploads.messages.errorLogError') as string);
            });
    }

    showPercentsProgressOrStatus(row: ServerFile): string {
        if (row.progress === '' || !isWritingToDatabase(row.statusMessage)) {
            return localizeFileStatusMessage(row.statusMessage) + '. ' + row.warnings;
        }
        return row.progress;
    }

    mounted() {
        if (this.type === FileType.COMPETITORS) {
            if (this.$disallowedModules.includes('competitors')) {
                this.$router.replace('/calc');
            }
        }
        EventBus.$on('file', this.onFileEventReceived);
        EventBus.$on('downloadProgress', this.onDownloadProgressReceived);

        const pager = { ...this.pagination };
        pager.current = 1;
        this.pagination = pager;
        this.getFiles({
            currentPage: this.pagination.current,
            perPage: this.pagination.pageSize,
        }, () => {})
    }

    created() {
        if (this.type === FileType.PROMO) {
            if (this.$disallowedModules.includes('download-promo')) {
                this.$router.replace('/files');
            }
        }
    }

    beforeDestroy() {
        EventBus.$off('file', this.onFileEventReceived);
        EventBus.$off('downloadProgress', this.onDownloadProgressReceived);
    }

    onFileEventReceived(d: ServerFile) {
        this.updateTableRowStatus(d);
    }

    private onDownloadProgressReceived(p: DownLoadPercent) {
        this.updateDownloadPercent(p);
    }

    getMarketFromLocalstorage(): SelectMarket | null {
        return localStorage.getItem('files_market') ? JSON.parse(localStorage.getItem('files_market')!) : null;
    }
}
